import React, { FC, useContext, useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { MobXProviderContext } from "mobx-react";
import { Controller, useForm } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { ResidentProfile } from "../../../models/residentProfile";
import { getErrorTitle } from "../../../constants/constants";
import { Toast } from "primereact/toast";
import { Checkbox } from "primereact/checkbox";
import {
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

interface EditResiModalProps {
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  data: ResidentProfile | null;
}

const EditResidentModal: FC<EditResiModalProps> = ({
  isOpen,
  toggleModal,
  flag,
  setFlag,
  data,
}) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const defaultValues = {
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    active: data?.active || false,
    isOnMedicaid: !!data?.isOnMedicaid,
    primaryPhysicianName: data?.primaryPhysicianName || '',
    primaryPhysicianClinic: data?.primaryPhysicianClinic || '',
    primaryPhysicianPhoneNumber: data?.primaryPhysicianPhoneNumber || '',
    primaryPhysicianFaxNumber: data?.primaryPhysicianFaxNumber || '',
    primaryPhysicianEmail: data?.primaryPhysicianEmail || '',
    primaryPhysicianAddress: data?.primaryPhysicianAddress || '',
    pharmacyName: data?.pharmacyName || '',
    pharmacyPhoneNumber: data?.pharmacyPhoneNumber || '',
    pharmacyFaxNumber: data?.pharmacyFaxNumber || '',
    pharmacyEmail: data?.pharmacyEmail || '',
    pharmacyAddress: data?.pharmacyAddress || '',
  }

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  useEffect(() => {
    const fetchData = async () => {
      if (!data) return;
      try {
        await backendAPIStore.init();
        reset({
          firstName: data?.firstName || '',
          lastName: data?.lastName || '',
          active: data?.active || false,
          isOnMedicaid: !!data?.isOnMedicaid,
          primaryPhysicianName: data?.primaryPhysicianName || '',
          primaryPhysicianClinic: data?.primaryPhysicianClinic || '',
          primaryPhysicianPhoneNumber: data?.primaryPhysicianPhoneNumber || '',
          primaryPhysicianFaxNumber: data?.primaryPhysicianFaxNumber || '',
          primaryPhysicianEmail: data?.primaryPhysicianEmail || '',
          primaryPhysicianAddress: data?.primaryPhysicianAddress || '',
          pharmacyName: data?.pharmacyName || '',
          pharmacyPhoneNumber: data?.pharmacyPhoneNumber || '',
          pharmacyFaxNumber: data?.pharmacyFaxNumber || '',
          pharmacyEmail: data?.pharmacyEmail || '',
          pharmacyAddress: data?.pharmacyAddress || '',
        });
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      }
    };
    fetchData();
  }, [data]);

  const onSubmit = async (formDataOnly: any) => {

    setLoading(true);
    try {
      const updatedData = { ...data, ...formDataOnly };
      await backendAPIStore.init();
      const response = await backendAPIStore.updateResident(updatedData);
      if (response) {
        reset();
        toggleModal(false);
        setFlag(!flag);
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const headerElement = renderSurfaceHeader(
    "Edit Resident Profile",
    "Update resident basics plus the shared physician and pharmacy contacts used across care plan and eMAR workflows.",
    "pi pi-user-edit",
  );

  const footerContent = renderDialogFooter({
    errorMessage,
    loading,
    onCancel: () => toggleModal(false),
    onSubmit: handleSubmit(onSubmit),
    submitLabel: "Save Changes",
  });

  return (
    <Dialog visible={isOpen} modal header={headerElement} footer={footerContent} style={appDialogStyle}
      headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
      onHide={() => { if (!isOpen) return; toggleModal(false); }}>
      <Toast ref={toast} />
      <form>
        <div style={appFormSectionStyle}>
          <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "1.5rem" }}>Profile</div>
          <div className="field" style={{ marginTop: "1rem" }}>
            <span className="p-float-label">
              <Controller name="firstName" control={control} rules={{
                required: 'Name is required.',
                maxLength: { value: 100, message: 'Name must be at most 100 characters long.' }
              }} render={({ field, fieldState }) => (
                <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
              <label htmlFor="firstName" className={classNames({ 'p-error': errors.firstName })}>First Name</label>
            </span>
            {getFormErrorMessage('firstName')}
          </div>
          <div className="field">
            <span className="p-float-label">
              <Controller name="lastName" control={control} rules={{
                required: 'Name is required.',
                maxLength: { value: 100, message: 'Name must be at most 100 characters long.' }
              }} render={({ field, fieldState }) => (
                <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
              <label htmlFor="lastName" className={classNames({ 'p-error': errors.lastName })}>Last Name</label>
            </span>
            {getFormErrorMessage('lastName')}
          </div>
          <div className="field">
            <div className="flex align-items-center gap-2">
              <Controller
                name="isOnMedicaid"
                control={control}
                render={({ field, fieldState }) => (
                  <Checkbox
                    inputId={field.name}
                    checked={!!field.value}
                    onChange={(e) => field.onChange(!!e.checked)}
                    className={classNames({ 'p-invalid': fieldState.invalid })}
                  />
                )}
              />
              <label htmlFor="isOnMedicaid" className={classNames({ 'p-error': errors.isOnMedicaid })}>
                Resident is on Medicaid
              </label>
            </div>
          </div>
          <div className="field">
            <div
              style={{
                border: "1px solid #fed7aa",
                borderRadius: "14px",
                background: "#fff7ed",
                padding: "0.9rem 1rem",
              }}
            >
              <div className="flex align-items-center gap-2">
                <Controller
                  name="active"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      inputId={field.name}
                      checked={!!field.value}
                      onChange={(e) => field.onChange(!!e.checked)}
                      className={classNames({ 'p-invalid': fieldState.invalid })}
                    />
                  )}
                />
                <label htmlFor="active" className={classNames({ 'p-error': errors.active })} style={{ fontWeight: 700, color: "#7c2d12" }}>
                  Keep this resident in the active list
                </label>
              </div>
              <div className="mt-2" style={{ color: "#9a3412", fontSize: "0.9rem", lineHeight: 1.55 }}>
                Uncheck this to archive the resident after move-out or discharge.
              </div>
              <div className="mt-2" style={{ color: "#7c2d12", fontSize: "0.84rem", lineHeight: 1.55 }}>
                Archived residents are hidden from active dashboards, but their binder and records stay available under the archived section.
              </div>
              <div className="mt-2" style={{ color: "#7c2d12", fontSize: "0.84rem", lineHeight: 1.55 }}>
                You can restore them later when a resident slot is available.
              </div>
            </div>
          </div>
        </div>

        <div style={appFormSectionStyle}>
          <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "1.5rem" }}>Clinical Contacts</div>
          <div style={{ color: "#64748b", fontSize: "0.9rem", lineHeight: 1.6, marginBottom: "1rem" }}>
            Save these once here so reports and care workflows can reuse the same physician and pharmacy details.
          </div>
          <div className="grid">
            <div className="col-12 md:col-6">
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "1rem" }}>Primary Physician</div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="primaryPhysicianName" control={control} rules={{
                    maxLength: { value: 120, message: 'Primary physician name must be at most 120 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="primaryPhysicianName" className={classNames({ 'p-error': errors.primaryPhysicianName })}>Physician Name</label>
                </span>
                {getFormErrorMessage('primaryPhysicianName')}
              </div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="primaryPhysicianClinic" control={control} rules={{
                    maxLength: { value: 120, message: 'Clinic name must be at most 120 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="primaryPhysicianClinic" className={classNames({ 'p-error': errors.primaryPhysicianClinic })}>Clinic / Practice</label>
                </span>
                {getFormErrorMessage('primaryPhysicianClinic')}
              </div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="primaryPhysicianPhoneNumber" control={control} rules={{
                    maxLength: { value: 40, message: 'Phone must be at most 40 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="primaryPhysicianPhoneNumber" className={classNames({ 'p-error': errors.primaryPhysicianPhoneNumber })}>Phone</label>
                </span>
                {getFormErrorMessage('primaryPhysicianPhoneNumber')}
              </div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="primaryPhysicianFaxNumber" control={control} rules={{
                    maxLength: { value: 40, message: 'Fax must be at most 40 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="primaryPhysicianFaxNumber" className={classNames({ 'p-error': errors.primaryPhysicianFaxNumber })}>Fax</label>
                </span>
                {getFormErrorMessage('primaryPhysicianFaxNumber')}
              </div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="primaryPhysicianEmail" control={control} rules={{
                    maxLength: { value: 120, message: 'Email must be at most 120 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="primaryPhysicianEmail" className={classNames({ 'p-error': errors.primaryPhysicianEmail })}>Email</label>
                </span>
                {getFormErrorMessage('primaryPhysicianEmail')}
              </div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="primaryPhysicianAddress" control={control} rules={{
                    maxLength: { value: 240, message: 'Address must be at most 240 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="primaryPhysicianAddress" className={classNames({ 'p-error': errors.primaryPhysicianAddress })}>Address</label>
                </span>
                {getFormErrorMessage('primaryPhysicianAddress')}
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "1rem" }}>Primary Pharmacy</div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="pharmacyName" control={control} rules={{
                    maxLength: { value: 120, message: 'Pharmacy name must be at most 120 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="pharmacyName" className={classNames({ 'p-error': errors.pharmacyName })}>Pharmacy Name</label>
                </span>
                {getFormErrorMessage('pharmacyName')}
              </div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="pharmacyPhoneNumber" control={control} rules={{
                    maxLength: { value: 40, message: 'Phone must be at most 40 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="pharmacyPhoneNumber" className={classNames({ 'p-error': errors.pharmacyPhoneNumber })}>Phone</label>
                </span>
                {getFormErrorMessage('pharmacyPhoneNumber')}
              </div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="pharmacyFaxNumber" control={control} rules={{
                    maxLength: { value: 40, message: 'Fax must be at most 40 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="pharmacyFaxNumber" className={classNames({ 'p-error': errors.pharmacyFaxNumber })}>Fax</label>
                </span>
                {getFormErrorMessage('pharmacyFaxNumber')}
              </div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="pharmacyEmail" control={control} rules={{
                    maxLength: { value: 120, message: 'Email must be at most 120 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="pharmacyEmail" className={classNames({ 'p-error': errors.pharmacyEmail })}>Email</label>
                </span>
                {getFormErrorMessage('pharmacyEmail')}
              </div>
              <div className="field">
                <span className="p-float-label">
                  <Controller name="pharmacyAddress" control={control} rules={{
                    maxLength: { value: 240, message: 'Address must be at most 240 characters long.' }
                  }} render={({ field, fieldState }) => (
                    <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                  )} />
                  <label htmlFor="pharmacyAddress" className={classNames({ 'p-error': errors.pharmacyAddress })}>Address</label>
                </span>
                {getFormErrorMessage('pharmacyAddress')}
              </div>
            </div>
          </div>
        </div>
      </form>
    </Dialog>
  );
};

export default EditResidentModal;
